import { useState, type ReactElement } from 'react';
import { MISCHMODI, MISCH_NAMEN, type Mischmodus, type Op } from '@schnitt/shared';
import { useEditor } from '../store';
import { Sheet, Slider } from './Sheet';

/**
 * Alles, was einer ganzen Spur gehört — und nicht dem einen Clip, den man
 * gerade angetippt hat.
 *
 * Der Pegel stand im Modell und wurde im Export und in der Vorschau gerechnet,
 * zu bedienen war er aber nirgends; das Absenken unter Sprache ließ sich nur
 * im Kontextmenü an- und ausschalten, ohne zu sagen, *wie weit*. Ein
 * Kontextmenü ist der falsche Ort für einen Regler: es schließt sich beim
 * ersten Tipp, ein Regler will gezogen werden. Also ein Blatt.
 */
export function SpurSheet({
  trackId,
  onClose,
  closeLabel,
}: {
  trackId: string;
  onClose: () => void;
  closeLabel?: string;
}): ReactElement {
  const timeline = useEditor((s) => s.timeline);
  const dispatch = useEditor((s) => s.dispatch);
  const role = useEditor((s) => s.role);
  const track = timeline.tracks.find((t) => t.id === trackId) ?? null;
  const [name, setName] = useState(track?.name ?? '');

  if (!track) {
    return (
      <Sheet title="Spur" onClose={onClose} closeLabel={closeLabel}>
        <p className="hint">Diese Spur gibt es nicht mehr.</p>
      </Sheet>
    );
  }

  const editable = role !== 'viewer';
  const patch = (werte: Record<string, unknown>) =>
    dispatch({ op: 'updateTrack', trackId, patch: werte } as Op);

  const clips = track.clips.length;
  // Die unterste Bildspur ist die Basis -- dieselbe Regel wie im Export.
  const istBasis = timeline.tracks.filter((t) => t.kind === 'video')[0]?.id === track.id;
  return (
    <Sheet title={track.name || track.id} onClose={onClose} closeLabel={closeLabel}>
      <p className="hint" style={{ marginTop: 0 }}>
        {track.kind === 'audio' ? 'Tonspur' : 'Bildspur'} · {clips === 1 ? '1 Clip' : `${clips} Clips`}
      </p>

      <label className="field">
        <span>Name</span>
        <input
          value={name}
          maxLength={80}
          disabled={!editable}
          onChange={(e) => setName(e.target.value)}
          onBlur={() => {
            if (name !== track.name) patch({ name: name.trim() });
          }}
          aria-label="Name der Spur"
        />
      </label>

      {/* Der Pegel der ganzen Spur. Er wird mit dem des Clips multipliziert --
          die Spur herunterzuziehen behält also das Verhältnis darin. */}
      <h3 style={{ fontSize: 13, color: 'var(--muted)', margin: '18px 0 8px' }}>Ton</h3>
      <Slider
        label="Pegel der Spur"
        value={track.gain}
        min={0}
        max={2}
        format={(v) => `${Math.round(v * 100)}%`}
        onChange={(gain) => patch({ gain })}
      />
      <p className="hint" style={{ marginTop: 0 }}>
        Wird mit dem Pegel jedes Clips verrechnet.
      </p>
      <button className="row" disabled={!editable} onClick={() => patch({ muted: !track.muted })}>
        {track.muted ? 'Stumm' : 'Ton an'}
      </button>

      {/* Ein Musikbett senkt sich, sobald anderswo gesprochen wird. Der Schalter
          stand schon im Kontextmenü; wie weit es absinkt, war nicht zu sagen. */}
      {track.kind === 'audio' ? (
        <>
          <h3 style={{ fontSize: 13, color: 'var(--muted)', margin: '18px 0 8px' }}>Unter Sprache</h3>
          <button className="row" disabled={!editable} onClick={() => patch({ duck: !track.duck })}>
            {track.duck ? 'Senkt sich ab' : 'Senkt sich nicht ab'}
          </button>
          {track.duck ? (
            <>
              <Slider
                label="Abgesenkt auf"
                value={track.duckLevel}
                min={0.05}
                max={1}
                step={0.05}
                format={(v) => `${Math.round(v * 100)}%`}
                onChange={(duckLevel) => patch({ duckLevel })}
              />
              <p className="hint" style={{ marginTop: 0 }}>
                So laut bleibt die Spur, während gesprochen wird.
              </p>
            </>
          ) : null}
        </>
      ) : null}

      {/* S10: Wie diese Spur auf der darunter liegt. Nur über der Basis --
          unter der untersten Bildspur liegt nichts, mit dem sich mischen
          liesse, und ein Knopf, der nichts tut, ist schlimmer als keiner. */}
      {track.kind === 'video' && !istBasis ? (
        <>
          <h3 style={{ fontSize: 13, color: 'var(--muted)', margin: '18px 0 8px' }}>Wie sie auf der Spur darunter liegt</h3>
          <div className="chips" data-mischmodi>
            {MISCHMODI.map((m: Mischmodus) => (
              <button
                key={m}
                className={`chip ${track.mischmodus === m ? 'active' : ''}`}
                data-mischmodus={m}
                disabled={!editable}
                onClick={() => patch({ mischmodus: m })}
              >
                {MISCH_NAMEN[m]}
              </button>
            ))}
          </div>
          <p className="hint">
            Normal deckt zu. Multiplizieren dunkelt ab, Aufhellen hellt auf, Weiches Licht tut beides
            sanft — je nachdem, was darunter liegt.
          </p>
        </>
      ) : null}

      <h3 style={{ fontSize: 13, color: 'var(--muted)', margin: '18px 0 8px' }}>Im Schnitt</h3>
      {track.kind === 'video' ? (
        <button className="row" disabled={!editable} onClick={() => patch({ hidden: !track.hidden })}>
          {track.hidden ? 'Ausgeblendet' : 'Sichtbar'}
        </button>
      ) : null}
      <button className="row" disabled={!editable} onClick={() => patch({ locked: !track.locked })}>
        {track.locked ? 'Gesperrt' : 'Offen'}
      </button>
      <p className="hint">Gesperrt heißt: kein Ziehen, kein Trennen, kein Löschen.</p>
    </Sheet>
  );
}
